import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { RefreshCw, ArrowDown } from 'lucide-react';
import { getThemeStyles } from '../../../themes/themeConfig';
import { useTranslation } from 'react-i18next';
import AppleSpinner from '../../../common/AppleSpinner';

// Importación de componentes de mensajes
const MagicalMessage = React.lazy(() => import('../../messages/MagicalMessage'));

// Memoización para optimizar renderizado
const MemoizedMagicalMessage = React.memo(
  ({
    content, isUser, isLastMessage, onFeedback, userId, messageId,
    isLoading, onUpdateMessage, lastImageRequestId, files, imageBase64Data, wasCancelled,
    timestamp, toolsUsed, activeTools, metadata,
    // Nuevas props para versionado
    baseMessageId, versionIndex, siblingVersions,
    onEditMessage, onResendMessage, onSwitchVersion, isLoadingVersion
  }) => (
    <React.Suspense fallback={<Box p={2}></Box>}>
      <MagicalMessage
        content={content}
        isUser={isUser}
        isLastMessage={isLastMessage}
        onFeedback={onFeedback}
        userId={userId}
        messageId={messageId}
        isLoading={isLoading}
        onUpdateMessage={onUpdateMessage}
        lastImageRequestId={lastImageRequestId}
        files={files}
        imageBase64Data={imageBase64Data}
        wasCancelled={wasCancelled}
        timestamp={timestamp}
        toolsUsed={toolsUsed}
        activeTools={activeTools}
        metadata={metadata}
        baseMessageId={baseMessageId}
        versionIndex={versionIndex}
        siblingVersions={siblingVersions}
        onEditMessage={onEditMessage}
        onResendMessage={onResendMessage}
        onSwitchVersion={onSwitchVersion}
        isLoadingVersion={isLoadingVersion}
      />
    </React.Suspense>
  )
);

// Estilos
const MessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  width: '100%',
  maxWidth: '800px',
  scrollBehavior: 'smooth',
  transition: 'all 0.3s ease-out',
  scrollbarWidth: 'thin',
  scrollbarColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1) transparent'
      : 'rgba(0, 0, 0, 0.1) transparent',
  msOverflowStyle: '-ms-autohiding-scrollbar',
  '& pre': {
    maxWidth: '100%',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
  '&::-webkit-scrollbar': {
    width: '8px !important',
    height: '8px !important',
  },
  '&::-webkit-scrollbar-track': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px !important',
  },
  '&::-webkit-scrollbar-thumb': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px !important',
    border: 'none !important',
    ':hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)',
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent !important',
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px 0',
  },
  ...getThemeStyles('MessagesContainer', theme.palette.mode),
}));

const ErrorMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 59, 48, 0.1)' 
    : 'rgba(255, 59, 48, 0.08)',
  borderRadius: '12px',
  ...getThemeStyles('ErrorMessage', theme.palette.mode),
}));

const RetryButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: '30px',
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7',
  color: theme.palette.primary.main,
  padding: '8px 16px',
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#E5E5EA',
  },
  ...getThemeStyles('RetryButton', theme.palette.mode),
}));

// Updated circular button similar to the red debug button - adapted for wallpaper mode
const ScrollToBottomButton = styled(Box)(({ theme }) => {
  // Verificar tanto actualMode como el data-theme del documento
  const dataTheme = document.documentElement.getAttribute('data-theme');
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper' ||
                          dataTheme === 'wallpaper' ||
                          dataTheme === 'wallpaper-light' ||
                          dataTheme === 'wallpaper-dark';
  const wallpaperColor = '#2C2C2E';

  return {
    position: 'fixed',
    bottom: '160px',
    right: '20px',
    width: '36px',
    height: '36px',
    backgroundColor: isWallpaperMode ? wallpaperColor : '#0385FF',
    color: '#FFFFFF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'pointer',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : 'none',
    boxShadow: isWallpaperMode
      ? 'none'
      : theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: isWallpaperMode ? '#3A3A3C' : '#0275E6',
    },
    '&:active': {
      transform: 'scale(0.95)',
      backgroundColor: isWallpaperMode ? '#48484A' : '#0264CC',
    },
    // Asegurar que el icono sea blanco en wallpaper
    '& svg': {
      color: '#FFFFFF',
    },
  };
});

// Nuevo componente para mostrar el spinner con estilo
const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '40%', // Posicionado al 40% desde arriba para compensar el espacio del input
  left: '50%',
  transform: 'translate(-50%, -50%)', // Centrado perfecto
  width: '100%',
  height: '100%', // Toma toda la altura disponible
  zIndex: 10,
  padding: 0,
  margin: 0,
  pointerEvents: 'none', // Para que no interfiera con interacciones del usuario
}));

/**
 * ChatMessages component - Displays message history in a scrollable container
 * 
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of message objects to display
 * @param {boolean} props.isWaitingForResponse - Whether waiting for a response
 * @param {boolean} props.isWaitingForServerResponse - Whether waiting for server response
 * @param {number} props.waitingStartTime - Timestamp when waiting started
 * @param {string} props.lastMessageId - ID of the last message
 * @param {Function} props.handleUpdateMessage - Function to update message
 * @param {string} props.lastImageRequestId - ID of last image request message
 * @param {Object} props.messageTypeInfo - Info about current message type
 * @param {Array} props.ultraThoughts - Array of "thinking" phrases for Ultra
 * @param {string|null} props.error - Error message, if any
 * @param {Function} props.handleRetry - Function to retry after error
 * @param {Object} props.user - Current user object
 * @param {Function} props.scrollToBottom - Function to scroll to bottom
 * @param {React.RefObject} props.userScrollingRef - Ref for tracking user scrolling
 * @param {React.RefObject} props.messagesEndRef - Ref for the end of messages
 * @param {Object} props.loadingStates - States indicating what's loading
 */
const ChatMessages = ({
  messages,
  isWaitingForResponse,
  isWaitingForServerResponse, // Nuevo prop para saber si estamos esperando respuesta del servidor
  isLoadingVersion, // Nuevo prop para saber si se está cargando una versión
  waitingStartTime,
  lastMessageId,
  handleUpdateMessage,
  lastImageRequestId,
  messageTypeInfo,
  ultraThoughts,
  activeTools, // Tools en ejecución para mostrar en tiempo real
  error,
  handleRetry,
  user,
  scrollToBottom,
  userScrollingRef,
  messagesEndRef,
  loadingStates,
  // Nuevos handlers para versionado
  onEditMessage,
  onResendMessage,
  onSwitchVersion
}) => {
  const { t } = useTranslation();
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const prevScrollTopRef = useRef(0);
  const actualScrollableContainerRef = useRef(null);

  // Robust scroll position check that finds the actual scrollable container
  const checkScrollPosition = useCallback(() => {
    // Get the ACTUAL scrollable container
    let container = messagesContainerRef.current;
    
    if (!container) return;
    
    // Find the actual scrollable parent if this element doesn't scroll
    if (container.scrollHeight <= container.clientHeight) {
      // This container doesn't scroll, try to find the scrollable parent
      let parent = container.parentElement;
      let foundScrollable = false;
      
      // Traverse up to find a scrollable parent
      while (parent && !foundScrollable) {
        if (parent.scrollHeight > parent.clientHeight && 
            (window.getComputedStyle(parent).overflowY === 'auto' || 
             window.getComputedStyle(parent).overflowY === 'scroll')) {
          container = parent;
          foundScrollable = true;
        } else {
          parent = parent.parentElement;
        }
      }
      
      // If still not found, try document.documentElement or body as fallback
      if (!foundScrollable) {
        if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
          container = document.documentElement;
        } else if (document.body.scrollHeight > document.body.clientHeight) {
          container = document.body;
        } else {
          return;
        }
      }
    }
    
    // Store the actual scrollable container for future use
    actualScrollableContainerRef.current = container;
   
    const { scrollHeight, scrollTop, clientHeight } = container;
    
    // Store previous scroll position
    prevScrollTopRef.current = scrollTop;
    
    // Calculate distance from bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // Show button when scrolled up more than 100px
    const showButton = distanceFromBottom > 100;
   
    // Update state only if it changes to avoid rerenders
    if (showButton !== showScrollButton) {
      setShowScrollButton(showButton);
    }
   
    // Update userScrollingRef to track if the user has scrolled
    if (distanceFromBottom > 20) {
      userScrollingRef.current = true;
    }
  }, [showScrollButton, userScrollingRef]);

  // Enhanced scroll handling that works with any container
  const handleScrollToBottom = useCallback(() => {
    // Use the cached scrollable container if available
    let container = actualScrollableContainerRef.current || messagesContainerRef.current;
    
    if (!container) return;
    
    // If we haven't found a scrollable container yet, or it's no longer valid, find it again
    if (!actualScrollableContainerRef.current || container.scrollHeight <= container.clientHeight) {
      container = messagesContainerRef.current;
      
      if (container.scrollHeight <= container.clientHeight) {
        let parent = container.parentElement;
        let foundScrollable = false;
        
        while (parent && !foundScrollable) {
          if (parent.scrollHeight > parent.clientHeight && 
              (window.getComputedStyle(parent).overflowY === 'auto' || 
               window.getComputedStyle(parent).overflowY === 'scroll')) {
            container = parent;
            foundScrollable = true;
          } else {
            parent = parent.parentElement;
          }
        }
        
        if (!foundScrollable) {
          if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
            container = document.documentElement;
          } else if (document.body.scrollHeight > document.body.clientHeight) {
            container = document.body;
          } else {
            return;
          }
        }
        
        // Update the ref
        actualScrollableContainerRef.current = container;
      }
    }
    
    // Scroll the ACTUAL scrollable container
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
   
    // Also use scrollIntoView as backup
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
   
    // Reset scroll tracking after animation completes
    setTimeout(() => {
      userScrollingRef.current = false;
      setShowScrollButton(false);
      checkScrollPosition(); // Recheck position after scroll
    }, 500);
  }, [checkScrollPosition, messagesEndRef, userScrollingRef]);

  // Render error message
  const renderErrorMessage = useCallback(() => {
    if (!error) return null;
    return (
      <ErrorMessage>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        {handleRetry && (
          <RetryButton
            variant="contained"
            startIcon={<RefreshCw size={16} />}
            onClick={handleRetry}
          >
            {t('chat.messages.retry')}
          </RetryButton>
        )}
      </ErrorMessage>
    );
  }, [error, handleRetry, t]);

  // Nuevo método para renderizar el spinner de carga estilo Apple
  const renderLoadingSpinner = useCallback(() => {
    return (
      <LoadingSpinner>
        <AppleSpinner size={36} />
      </LoadingSpinner>
    );
  }, []);

  // Add document-wide scroll detection
  useEffect(() => {
    // Monitor ALL possible scroll containers
    const handleAnyScroll = () => {
      // Use requestAnimationFrame to prevent performance issues
      requestAnimationFrame(() => {
        checkScrollPosition();
      });
    };
    
    // Add scroll listeners to window and document
    window.addEventListener('scroll', handleAnyScroll, { passive: true });
    document.addEventListener('scroll', handleAnyScroll, { passive: true });
    
    // Also check on any user interaction that might cause scrolling
    document.addEventListener('wheel', handleAnyScroll, { passive: true });
    document.addEventListener('touchmove', handleAnyScroll, { passive: true });
    document.addEventListener('keydown', handleAnyScroll, { passive: true });
    
    // Poll regularly to catch any missed events
    const intervalId = setInterval(checkScrollPosition, 250);
    
    // Initial check
    checkScrollPosition();
    
    return () => {
      window.removeEventListener('scroll', handleAnyScroll);
      document.removeEventListener('scroll', handleAnyScroll);
      document.removeEventListener('wheel', handleAnyScroll);
      document.removeEventListener('touchmove', handleAnyScroll);
      document.removeEventListener('keydown', handleAnyScroll);
      clearInterval(intervalId);
    };
  }, [checkScrollPosition]);

  // Add resize listener to catch layout changes
  useEffect(() => {
    const handleResize = () => {
      checkScrollPosition();
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [checkScrollPosition]);

  // Add DOM mutation observer to detect content changes
  useEffect(() => {
    // Create a mutation observer to detect when the DOM changes
    const mutationObserver = new MutationObserver(() => {
      checkScrollPosition();
    });
    
    // Target the main container and monitor for child changes
    if (messagesContainerRef.current) {
      mutationObserver.observe(messagesContainerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: false
      });
    }
    
    return () => {
      mutationObserver.disconnect();
    };
  }, [checkScrollPosition]);

  // Enhanced auto-scroll logic - como Claude.ai
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isAssistantResponding = !lastMessage?.isUser;

      // MODIFICADO: NO hacer scroll automático cuando el usuario envía mensaje
      // Eso se maneja en ChatViewNest con scrollToNewMessage()

      // Auto-scroll suave mientras el asistente responde (si el usuario no scrolleó manualmente)
      if (isAssistantResponding && !userScrollingRef.current) {
        scrollToBottom({ force: false }); // Scroll suave hacia abajo durante respuesta
      }
    }
  }, [messages, scrollToBottom, userScrollingRef]);

  // Auto-scroll adicional mientras está esperando respuesta
  useEffect(() => {
    if (isWaitingForResponse && !userScrollingRef.current) {
      const scrollInterval = setInterval(() => {
        scrollToBottom({ force: false });
      }, 100); // Cada 100ms para mantener el scroll durante streaming

      return () => clearInterval(scrollInterval);
    }
  }, [isWaitingForResponse, scrollToBottom, userScrollingRef]);

  return (
    <MessagesContainer ref={messagesContainerRef}>
      {/* Mostrar spinner de carga cuando no hay mensajes y está cargando */}
      {messages.length === 0 && (loadingStates?.messages || isLoadingVersion) && renderLoadingSpinner()}

      {/* Mostrar mensajes normalmente cuando hay contenido */}
      {messages.map((message, index) => {
        const isLastAssistantMessage =
          !message.isUser && message.id === lastMessageId;

        // Log para debugging de tools
        if (!message.isUser && message.toolsUsed) {
          console.log('🔍 [ChatMessages] Pasando mensaje con tools:', {
            messageId: message.id,
            toolsUsed: message.toolsUsed,
            toolsCount: message.toolsUsed?.length
          });
        }

        // IMPORTANTE: Incluir activeTools en el key para forzar re-render cuando cambian
        // Esto permite que el ToolIndicator se muestre inmediatamente cuando se detecta una tool
        const baseKey = message.uiId || message.id || index;
        const toolsKey = isLastAssistantMessage && activeTools && activeTools.length > 0
          ? `-tools-${activeTools.length}-${activeTools.map(t => t.status).join('-')}`
          : '';
        const messageKey = `${baseKey}${toolsKey}`;

        return (
          <MemoizedMagicalMessage
            key={messageKey}
            content={message.text}
            isUser={message.isUser}
            isLastMessage={isLastAssistantMessage}
            onFeedback={() => {}}
            userId={user?.uid}
            messageId={message.id}
            isLoading={message.isLoading}
            onUpdateMessage={handleUpdateMessage}
            lastImageRequestId={lastImageRequestId}
            files={message.files}
            imageBase64Data={message.imageBase64Data}
            wasCancelled={message.wasCancelled}
            timestamp={message.timestamp}
            toolsUsed={message.toolsUsed}
            activeTools={isLastAssistantMessage ? activeTools : undefined} // Solo pasar activeTools al último mensaje
            metadata={message.metadata} // Pasar metadata para acceder a executedTools
            baseMessageId={message.baseMessageId}
            versionIndex={message.versionIndex}
            siblingVersions={message.siblingVersions}
            onEditMessage={onEditMessage}
            onResendMessage={onResendMessage}
            onSwitchVersion={onSwitchVersion}
            isLoadingVersion={isLoadingVersion}
          />
        );
      })}

      {renderErrorMessage()}

      {/* Botón de scroll */}
      {showScrollButton && (
        <ScrollToBottomButton
          onClick={handleScrollToBottom}
          role="button"
          aria-label={t('chat.messages.scrollToBottom')}
        >
          <ArrowDown size={24} style={{ color: '#FFFFFF' }} />
        </ScrollToBottomButton>
      )}

      <div ref={messagesEndRef} />
    </MessagesContainer>
  );
};

export default React.memo(ChatMessages);
